import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import HelpIcon from '../../../components/core/HelpIcon/HelpIcon';
import SectionCard from '../../../components/SectionCard/SectionCard';
import BarGraph from '../../../components/Graphs/BarGraph/BarGraph';
import ProgressBar from '../../../components/Graphs/ProgressBar/ProgressBar';
import { getStatusColor, getChipTextColor } from '../../../utils/utilFunctions';

import StyledWrapper from '../style';

import { Box, CircularProgress, Chip, Tooltip } from '@mui/material';
import {
	ContentCopy as ContentCopyIcon,
	KeyboardArrowRight as KeyboardArrowRightIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon,
	Launch as LaunchIcon
} from '@mui/icons-material';

import { getGNURL } from '../../../utils/environmentUtils';
import { numberToMoney } from '../../../utils/numberUtils';

const InfoCard = ({ data, width, isFetching }) => {
	let { programID } = useParams();
	programID = programID?.replace('p-', '');

	const [fundedDropdown, setFundedDropdown] = useState(false);
	const [progressBarData, setProgressBarData] = useState({});
	const [BarGraphData, setBarGraphData] = useState([]);
	const BarGraphKeys = ['value'];

	useEffect(() => {
		if (data?.start_date && data?.end_date) {
			setProgressBarData({
				startDate: new Date(data?.start_date),
				endDate: new Date(data?.end_date),
				isCountdown: false
			});
		}

		setBarGraphData([
			{
				item: 'Requested',
				value: data?.requested_funds || 1,
				color: '#FFA726'
			},
			{
				item: 'Available',
				value: data?.available_funds || 1,
				color: '#8C9EFF'
			}
		]);
	}, [data]);

	const solicitationURL = data?.solicitationId
		? `${getGNURL()}/GO/srmng_solicitationEdit/SOLICITATIONID/${data?.solicitationId}`
		: '-';
	const solicitationListing = data?.redirectionToken
		? `${getGNURL()}/GO/gn_redir/T/${data?.redirectionToken}`
		: '-';
	const hasEnterpriseReportingAccess = data?.has_enterprise_reporting_access === true;

	return (
		<SectionCard width={width} height={'fit-content'}>
			{isFetching ? (
				<Box
					sx={{
						display: 'flex',
						width: '100%',
						height: '100%',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					<CircularProgress />
				</Box>
			) : (
				<>
					<StyledWrapper.CardTitleContainer>
						{data?.status && (
							<Chip
								sx={{
									backgroundColor: getStatusColor(data?.status),
									color: getChipTextColor(data?.status)
								}}
								label={data?.status}
								color="primary"
								size="small"
							/>
						)}
						<Tooltip
							title={
								<StyledWrapper.CenteredText
									isLink={true}
									onClick={() => window.location.replace(getGNURL())}
								>
									Edit Program in Grants Network <LaunchIcon />
								</StyledWrapper.CenteredText>
							}
							placement="top"
							arrow
						>
							<StyledWrapper.CardTitle
								to={`/programs/p-${programID}/application-setup/program%20details`}
								data-testid={'cardinfo-title'}
							>
								{data?.title || '-'}
							</StyledWrapper.CardTitle>
						</Tooltip>
					</StyledWrapper.CardTitleContainer>

					<StyledWrapper.FullContainer>
						<StyledWrapper.HalfContainer>
							<StyledWrapper.CardRowContainerLeft>
								<StyledWrapper.CardRowTitle data-testid={'department-title'}>
									Department{' '}
									<HelpIcon
										hint={'The Grants Network department to which this program is assigned.'}
									/>
								</StyledWrapper.CardRowTitle>
								<Tooltip arrow placement="top" title={data?.department || '-'}>
									<StyledWrapper.CardRowInfo data-testid={'department-value'}>
										{data?.department || '-'}
									</StyledWrapper.CardRowInfo>
								</Tooltip>
							</StyledWrapper.CardRowContainerLeft>

							<StyledWrapper.CardRowContainerLeft>
								<StyledWrapper.CardRowTitle data-testid={'requested-title'}>
									Requested vs Available{' '}
									<HelpIcon
										hint={
											'The amount of funding currently included in submitted applications versus the total amount of funding available for this program.'
										}
									/>
								</StyledWrapper.CardRowTitle>

								<StyledWrapper.CardRowDataSet data-testid={'requested-value-container'}>
									{data?.require_budget && (data?.available_funds || data?.requested_funds) ? (
										<>
											{`${numberToMoney(data?.requested_funds || '$0.00')}`}
											<div data-testid={'nivo-bar-graph'} style={{ width: '80px', height: '30px' }}>
												<BarGraph data={BarGraphData} keys={BarGraphKeys} />
											</div>
											{`${numberToMoney(data?.available_funds || '$0.00')}`}
										</>
									) : (
										'-'
									)}
								</StyledWrapper.CardRowDataSet>
							</StyledWrapper.CardRowContainerLeft>

							<StyledWrapper.CardRowContainerLeft>
								<StyledWrapper.CardRowTitle data-testid={'app-period-title'}>
									Application Period{' '}
									<HelpIcon hint={'The time frame when applications can be submitted.'} />
								</StyledWrapper.CardRowTitle>
								<StyledWrapper.CardRowInfo data-testid={'app-period-progress'}>
									{progressBarData ? <ProgressBar data={progressBarData} /> : '-'}
								</StyledWrapper.CardRowInfo>
							</StyledWrapper.CardRowContainerLeft>

							<StyledWrapper.CardRowContainerLeft>
								<StyledWrapper.CardRowTitle data-testid={'solicitation-listing'}>
									Solicitation Listing{' '}
									<HelpIcon
										hint={'Link to the Public Solicitation Listing page for this program.'}
									/>
								</StyledWrapper.CardRowTitle>
								<StyledWrapper.GroupTextAndIcon width={'16rem'}>
									<Tooltip arrow placement="top" title={solicitationListing}>
										<StyledWrapper.CardRowInfoLink
											onClick={(e) => {
												e.preventDefault();
												window.open(solicitationListing, '_blank');
											}}
											width={'fit-content'}
											maxWidth={'80%'}
											to={solicitationListing}
											data-testid={'solicitation-value'}
										>
											{solicitationListing}
										</StyledWrapper.CardRowInfoLink>
									</Tooltip>
									{solicitationListing != '-' && (
										<StyledWrapper.CopyButton
											position={'relative'}
											aria-label="copy"
											onClick={() => {
												navigator.clipboard.writeText(solicitationListing);
											}}
										>
											<ContentCopyIcon />
										</StyledWrapper.CopyButton>
									)}
								</StyledWrapper.GroupTextAndIcon>
							</StyledWrapper.CardRowContainerLeft>

							<StyledWrapper.CardRowContainerLeft>
								<StyledWrapper.CardRowTitle data-testid={'solicitation-title'}>
									Edit Solicitation{' '}
									<HelpIcon
										hint={'Link to edit this solicitation configuration in Grants Network.'}
									/>
								</StyledWrapper.CardRowTitle>
								<StyledWrapper.GroupTextAndIcon width={'16rem'}>
									<Tooltip arrow placement="top" title={solicitationURL}>
										<StyledWrapper.CardRowInfoLink
											onClick={(e) => {
												e.preventDefault();
												window.open(solicitationURL, '_blank');
											}}
											width={'fit-content'}
											maxWidth={'80%'}
											to={solicitationURL}
											data-testid={'solicitation-value'}
										>
											{solicitationURL}
										</StyledWrapper.CardRowInfoLink>
									</Tooltip>
									{data?.solicitationId && (
										<StyledWrapper.CopyButton
											position={'relative'}
											aria-label="copy"
											onClick={() => {
												navigator.clipboard.writeText(solicitationURL);
											}}
										>
											<ContentCopyIcon />
										</StyledWrapper.CopyButton>
									)}
								</StyledWrapper.GroupTextAndIcon>
							</StyledWrapper.CardRowContainerLeft>
						</StyledWrapper.HalfContainer>

						<StyledWrapper.HalfContainer>
							<StyledWrapper.CardRowContainerLeft>
								<StyledWrapper.CardRowTitle data-testid={'budget-title'}>
									Budget Template{' '}
									<HelpIcon hint={'The budget template included for this program.'} />
								</StyledWrapper.CardRowTitle>
								<Tooltip arrow placement="top" title={data?.budget_template?.title || '-'}>
									<StyledWrapper.CardRowInfo data-testid={'budget-value'}>
										{data?.budget_template?.title || '-'}
									</StyledWrapper.CardRowInfo>
								</Tooltip>
							</StyledWrapper.CardRowContainerLeft>

							<StyledWrapper.CardRowContainerLeft>
								<StyledWrapper.CardRowTitle data-testid={'ancestry-title'}>
									Program Type{' '}
									<HelpIcon
										hint={
											'Program type is selected when creating your program. A subprogram is a program whose funding source(s), budget template, and goals are inherited by the parent program selected in the dropdown menu. Once created, you can connect awards between the Parent Program and subrecipient awards in Subprogram.'
										}
									/>
								</StyledWrapper.CardRowTitle>
								<Tooltip arrow placement="top" title={data?.ancestory?.title || '-'}>
									<StyledWrapper.CardRowInfo data-testid={'ancestry-value'}>
										{data?.ancestry?.title || data?.ancestory?.title
											? 'Child Program'
											: data?.is_parent
											? 'Parent Program'
											: 'N/A'}
									</StyledWrapper.CardRowInfo>
								</Tooltip>
							</StyledWrapper.CardRowContainerLeft>

							<StyledWrapper.CardRowContainerLeft dropdownstate={fundedDropdown}>
								<StyledWrapper.CardRowTitle data-testid={'fundedby-title'}>
									{`Funded by ${data?.funded_by?.length || 0} Projects`}{' '}
									<HelpIcon hint={'Funding sources supporting this program.'} />
								</StyledWrapper.CardRowTitle>
								{data?.funded_by?.length !== 0 ? (
									<>
										<StyledWrapper.GroupTextAndIcon width={'16rem'}>
											<Tooltip
												arrow
												placement="top"
												title={data?.funded_by?.map((e, index) => (
													<>
														{e?.accountTitle || e?.extGrantName || ' Unspecified '}{' '}
														{data?.funded_by?.length - 1 !== index && ', '}
													</>
												))}
											>
												<StyledWrapper.CardRowMultiLinks
													width={'fit-content'}
													maxWidth={'80%'}
													dropdownstate={fundedDropdown}
												>
													{data?.funded_by?.map((e, index) => {
														return (
															<span key={`fundedBy-link-${index}`}>
																{e?.accountTitle || e?.extGrantName || ' Unspecified '}
																{data?.funded_by?.length - 1 !== index && ', '}
															</span>
														);
													})}
												</StyledWrapper.CardRowMultiLinks>
											</Tooltip>
											<StyledWrapper.CopyButton
												position={'relative'}
												aria-label="copy"
												onClick={() => {
													setFundedDropdown(!fundedDropdown);
												}}
											>
												{fundedDropdown ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
											</StyledWrapper.CopyButton>
										</StyledWrapper.GroupTextAndIcon>
									</>
								) : (
									<StyledWrapper.CardRowInfo data-testid={'fundedBy-value'}>
										{'-'}
									</StyledWrapper.CardRowInfo>
								)}
							</StyledWrapper.CardRowContainerLeft>

							{hasEnterpriseReportingAccess && (
								<StyledWrapper.CardRowContainerLeft>
									<StyledWrapper.CardRowTitle data-testid={'access-reports-title'}>
										Access Reports{' '}
										<HelpIcon
											hint={'To access reporting for your programs, visit Enterprise Reporting. '}
										/>
									</StyledWrapper.CardRowTitle>
									<Tooltip arrow placement="top" title={'Grants Network Enterprise Reports'}>
										<StyledWrapper.CardRowInfoLink
											onClick={(e) => {
												e.preventDefault();
												window.open(`${getGNURL()}/GO/rpt_enterpriseReporting`, '_blank');
											}}
											data-testid={'access-reports-value'}
										>
											{'Grants Network Enterprise Reports'}
										</StyledWrapper.CardRowInfoLink>
									</Tooltip>
								</StyledWrapper.CardRowContainerLeft>
							)}
						</StyledWrapper.HalfContainer>
					</StyledWrapper.FullContainer>
				</>
			)}
		</SectionCard>
	);
};

export default InfoCard;
